// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Single source of truth for the icon-rail items and the contextual subnav,
// per persona. Both IconRail and ContextualSubnav read from here so the shell
// stays consistent. Mirrors the mockup's rail + subnavItems.

import type { ComponentType } from 'react';
import {
  Dashboard,
  ChartRelationship,
  Book,
  DocumentTasks,
  Meter,
  CloudUpload,
  Money,
  UserMultiple,
  ChartColumn,
} from '@carbon/icons-react';
import type { Persona } from '../../stores/shellStore';
import type { AlarmBucket } from '../../types/alarm';

export type Section =
  | 'home'
  | 'site'
  | 'log'
  | 'audit'
  | 'fleet'
  | 'optimization'
  | 'onboarding'
  | 'bills'
  | 'users'
  | 'access'
  | 'executive';

export interface RailItem {
  section: Section;
  label: string;
  icon: ComponentType<{ size?: number }>;
  to: string;
  /** Route prefix used to compute the active state. */
  match: string;
  /** Requires admin (on top of the Manager persona). */
  adminOnly?: boolean;
}

export interface SubnavItem {
  label: string;
  to: string;
  /** Alarm bucket whose count renders as a pill. */
  bucket?: AlarmBucket;
  /** Show the live count of HITL plan approvals still pending review, instead of an alarm bucket count. */
  pendingApprovals?: boolean;
  /** Accent color var for the count pill. */
  accent?: string;
}

/**
 * Icon rail for a persona. `siteId` scopes the Site link. When `isFleet` is
 * true (manager viewing the whole fleet) the rail shows the **Fleet** item in
 * place of **Site**; a specific site selection shows Site.
 */
export function railItemsFor(persona: Persona, siteId: string, isFleet = false): RailItem[] {
  const siteOrFleet: RailItem = isFleet
    ? { section: 'fleet', label: 'Fleet', icon: ChartColumn, to: '/fleet', match: '/fleet' }
    : {
        section: 'site',
        label: 'Site',
        icon: ChartRelationship,
        to: `/site/${siteId}/kg`,
        match: '/site',
      };
  if (persona === 'executive') {
    // Executives are read-only / posture-only. User management and Access are
    // admin-guarded routes, so they are intentionally omitted here (they would
    // otherwise redirect executives away).
    return [
      {
        section: 'executive',
        label: 'Fleet posture',
        icon: ChartColumn,
        to: '/executive',
        match: '/executive',
      },
      { section: 'bills', label: 'Bills', icon: Money, to: '/bills', match: '/bills' },
    ];
  }

  if (persona === 'manager') {
    return [
      { section: 'home', label: 'Home', icon: Dashboard, to: '/home', match: '/home' },
      siteOrFleet,
      { section: 'log', label: 'Log', icon: Book, to: '/log', match: '/log' },
      { section: 'audit', label: 'Audit', icon: DocumentTasks, to: '/audit', match: '/audit' },
      {
        section: 'optimization',
        label: 'Optimization',
        icon: Meter,
        to: '/optimizations',
        match: '/optimizations',
      },
      {
        section: 'onboarding',
        label: 'Onboarding',
        icon: CloudUpload,
        to: '/onboarding',
        match: '/onboarding',
      },
      { section: 'bills', label: 'Bills', icon: Money, to: '/bills', match: '/bills' },
      {
        section: 'users',
        label: 'Users & access',
        icon: UserMultiple,
        to: '/users',
        match: '/users',
        adminOnly: true,
      },
    ];
  }

  // operator
  return [
    { section: 'home', label: 'Home', icon: Dashboard, to: '/home', match: '/home' },
    siteOrFleet,
    { section: 'log', label: 'Log', icon: Book, to: '/log', match: '/log' },
    { section: 'audit', label: 'Audit', icon: DocumentTasks, to: '/audit', match: '/audit' },
  ];
}

/** Contextual subnav for a section. Empty array → no subnav column. */
export function subnavFor(
  section: Section,
  siteId: string
): { title: string; items: SubnavItem[] } {
  switch (section) {
    case 'home':
      return {
        title: 'Alarms',
        items: [
          { label: 'All', to: '/home/all' },
          { label: 'Critical', to: '/home/critical', bucket: 'critical', accent: 'var(--sev1)' },
          { label: 'High', to: '/home/high', bucket: 'high', accent: 'var(--sev2)' },
          { label: 'Daily / Compliance', to: '/home/daily' },
          { label: 'Closed', to: '/home/closed', bucket: 'closed' },
        ],
      };
    case 'site':
      return {
        title: 'Site',
        items: [
          { label: 'Knowledge Graph', to: `/site/${siteId}/kg` },
          { label: 'Assets', to: `/site/${siteId}/assets` },
          { label: 'Floor plan', to: `/site/${siteId}/floor` },
          { label: 'Knowledge Base', to: `/site/${siteId}/kb` },
        ],
      };
    case 'fleet':
      return {
        title: 'Fleet',
        items: [
          { label: 'Fleet overview', to: '/fleet' },
          { label: 'Site detail', to: '/fleet/sites' },
          { label: 'SOP governance', to: '/fleet/sop' },
          { label: 'Helios Audit', to: '/fleet/audit' },
        ],
      };
    default:
      return { title: '', items: [] };
  }
}
